import React, { useState } from 'react'
import { Table, Button, Row, Col, Form, Pagination, Card } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from './../components/Message'
import Loader from './../components/Loader'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { useGetSalesQuery, useDeleteSaleMutation } from './../slices/salesApiSlice'
import { toast } from 'react-toastify'

const StaffSale = () => {
  // Auto-load today's date
  const today = new Date().toISOString().slice(0, 10)

  const [filter, setFilter] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, error, refetch } = useGetSalesQuery(pageNumber)
  const [deleteSale, { isLoading: loadingDelete }] = useDeleteSaleMutation()

  // Quick date filters
  const setToday = () => {
    setStartDate(today)
    setEndDate(today)
  }

  const setThisWeek = () => {
    const now = new Date()
    const firstDay = new Date(now.setDate(now.getDate() - now.getDay()))
    setStartDate(firstDay.toISOString().slice(0, 10))
    setEndDate(today)
  }

  const setThisMonth = () => {
    const firstDay = new Date()
    firstDay.setDate(1)
    setStartDate(firstDay.toISOString().slice(0, 10))
    setEndDate(today)
  }
  
  const setYesterday = () => {
  const y = new Date()
  y.setDate(y.getDate() - 1)
  const yesterday = y.toISOString().slice(0, 10)

  setStartDate(yesterday)
  setEndDate(yesterday)
}

const setLast2Days = () => {
  const now = new Date()

  const twoDaysAgo = new Date()
  twoDaysAgo.setDate(now.getDate() - 2)

  setStartDate(twoDaysAgo.toISOString().slice(0, 10))
  setEndDate(today)
}


  // Filters
  const filterSales = () => {
    if (!data?.sales) return []

    let result = data.sales.filter((sale) => {
      const d = new Date(sale.date)
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate || d <= new Date(endDate))
      )
    })

    if (filter.trim()) {
      const lf = filter.toLowerCase()
      result = result.filter((sale) => {
        return (
          sale.note?.toLowerCase().includes(lf) ||
          sale.username?.toLowerCase().includes(lf) ||
          sale.customerDetails?.name?.toLowerCase().includes(lf) ||
          sale.saleItems.some((it) => it.name.toLowerCase().includes(lf))
        )
      })
    }

    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase()
      result = result
        .map((sale) => ({
          ...sale,
          saleItems: sale.saleItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((sale) => sale.saleItems.length > 0)
    }

    return result
  }

  // Totals
  const totalSales = filterSales().reduce(
    (sum, sale) =>
      sum + sale.saleItems.reduce((sub, it) => sub + it.qty * it.price, 0),
    0
  )

  const totalQtys = filterSales().reduce(
    (sum, sale) =>
      sum + sale.saleItems.reduce((sub, it) => sub + it.qty, 0),
    0
  )

  // Excel Export
  const exportToExcel = () => {
    const rows = filterSales().flatMap((sale) =>
      sale.saleItems.map((item) => ({
        Date: new Date(sale.date).toLocaleDateString(),
        Product: item.name,
        Quantity: item.qty,
        Total: item.qty * item.price,
        User: sale.username || 'N/A',
        Customer: sale.customerDetails?.name || 'N/A',
        Note: sale.note || 'N/A',
      }))
    )

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales')

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Staff_Sales.xlsx')
  }

  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Delete this sale?')) {
      try {
        await deleteSale(id).unwrap()
        toast.success('Sale deleted')
        refetch()
      } catch (err) {
        toast.error(err?.data?.message || 'Delete failed')
      }
    }
  }

  // Pagination handler
  const goPage = (p) => setPageNumber(p)

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col><h1>Sales</h1></Col>
        <Col className="text-end">
          <Button variant="success" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </Col>
      </Row>

      {/* Quick Buttons */}
      <Row className="mb-3">
        <Col>
          <Button variant="secondary" className="me-2" onClick={setToday}>Today</Button>
          <Button variant="secondary" className="me-2" onClick={setYesterday}>Yesterday</Button>
          <Button variant="secondary" className="me-2" onClick={setLast2Days}>Last 2 Days</Button>
          <Button variant="secondary" className="me-2" onClick={setThisWeek}>This Week</Button>
          <Button variant="secondary" onClick={setThisMonth}>This Month</Button>
        </Col>

        <Col className="text-end">
          <Button
            variant="outline-dark"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </Col>
      </Row>

      {/* Collapsible Filter Panel */}
      {showFilters && (
        <Card className="p-3 mb-4">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Filter note/user/customer"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Product only"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Totals */}
      <Row >
        <Col md={6} className="text-end">
          <h4>Total Sales: D{totalSales.toFixed(2)}</h4>
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Qty: D{totalQtys.toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error fetching sales'}
        </Message>
      ) : (
        <>
          {/* Table */}
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>USER</th>
                <th>CUSTOMER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterSales().map((sale) =>
                sale.saleItems.map((item) => (
                  <tr key={`${sale._id}-${item.product}`}>
                    <td>{new Date(sale.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>D{(item.qty * item.price).toFixed(2)}</td>
                    <td>{sale.username || 'N/A'}</td>
                    <td>{sale.customerDetails?.name || 'N/A'}</td>

                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt=""
                          style={{
                            width: 10,
                            height: 10,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </td>

                    <td>
                      <Button
                        as={Link}
                        to={`/sale/${sale._id}`}
                        variant="light"
                        size="sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => deleteHandler(sale._id)}
                        >
                        Delete
                        </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => goPage(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StaffSale
